import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ComplianceData, RiskData } from '../models/compliance-data.model';
import { RiskScoreService } from './risk-score.service';
import { Region, RegionListResponse } from '../models/region.model';
import { THERAPEUTIC_AREAS } from '../data/therapeutic-areas';
import { environment } from '../../../environments/environment';
import { FilterStateService } from './filter-state.service';
import { Market } from '../models/market.model';
import {
  TopVendorsDataResponse,
  FlagType,
} from '../models/top-vendors-data.model';
import { EmptyVendorFlagsPercentages } from '../models/percentages-response.model';
import ComplianceElements from '../data/compliance-elements';
import { AvailableMonthsResponse } from '../models/available-months.model';

@Injectable({
  providedIn: 'root',
})
export class LambdaApiService {
  private readonly apiUrl = environment.apiGatewayUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly riskScoreService: RiskScoreService,
    private readonly filterStateService: FilterStateService,
  ) {}

  private regions: Region[] = [];
  private markets: Market[] = [];

  /**
   * gets compliance data from api.
   * filters are applied from filterData property.
   * gets cached data from session storage if available.
   * @returns
   */
  async fetchComplianceData(): Promise<RiskData> {
    const url = `${this.apiUrl}risk-analysis/regions`;
    const queryParams = this.filterStateService.filterAsParams;

    const responseAsObs = this.http.get<RiskData>(url, { params: queryParams });
    const data = await firstValueFrom(responseAsObs);
    return data;
  }

  getData(
    therapeuticArea: string,
    elementName: string,
  ): Promise<ComplianceData> {
    //mock data for MVP
    const mockData = {
      id: '0',
      therapeuticArea: THERAPEUTIC_AREAS[0],
      elements: ComplianceElements.map((el) => {
        const color = this.riskScoreService.calculatePercentageColor(
          EmptyVendorFlagsPercentages,
        );

        return {
          elementName: el.name,
          exec_summ: el.exec_summ,
          percentages: EmptyVendorFlagsPercentages,
          value: 0,
          color: color.color,
          colorName: color.colorName,
          link: el.link,
          icon: el.icon,
          description: el.description,
        };
      }),
    };

    const data: ComplianceData = { ...mockData };

    return Promise.resolve(data);
  }

  async getRegions(): Promise<Region[]> {
    if (this.regions.length > 0) {
      return this.regions;
    }

    const cacheKey = 'regions_data';
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const cachedData = JSON.parse(cached) as {
        regions: Region[];
        markets: Market[];
      };
      this.regions = cachedData.regions;
      this.markets = cachedData.markets;
      return this.regions;
    }

    const url = `${this.apiUrl}regions`;
    const responseAsObs = this.http.get<RegionListResponse>(url);
    const regions = await firstValueFrom(responseAsObs);
    const markets: Market[] = [];
    this.regions = regions.regions.map((region) => ({
      id: region.code,
      name: region.name,
    }));
    let iterator = 0;
    regions.regions.forEach((region) => {
      region.markets.forEach((market) => {
        iterator += 1;
        markets.push({
          name: market.name,
          region: region.code,
          code: market.code,
          id: iterator,
        });
      });
    });
    this.markets = markets;

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({ regions: this.regions, markets: this.markets }),
    );

    return this.regions;
  }

  async getMarkets(): Promise<Market[]> {
    if (this.markets.length > 0) {
      return this.markets;
    }

    await this.getRegions();

    return this.markets;
  }

  async getTopVendors(flagType: FlagType): Promise<TopVendorsDataResponse> {
    const queryParams = this.filterStateService.filterAsParams.set(
      'flag_type',
      flagType,
    );

    const url = `${this.apiUrl}risk-analysis/top-vendors`;
    const responseAsObs = this.http.get<TopVendorsDataResponse>(url, {
      params: queryParams,
    });
    const data = await firstValueFrom(responseAsObs);
    return data;
  }

  getPercentages(flagType: FlagType): Promise<any> {
    const url = `${this.apiUrl}risk-analysis/percentages`;
    const queryParams = this.filterStateService.filterAsParams.set(
      'flag_type',
      flagType,
    );

    const responseAsObs = this.http.get(url, { params: queryParams });
    return firstValueFrom(responseAsObs);
  }

  /**
   * Fetches percentages for a specific region or market (for compare view)
   */
  getPercentagesForCompare(
    flagType: FlagType,
    compareType: 'region' | 'market',
    value: string,
  ): Promise<any> {
    const url = `${this.apiUrl}risk-analysis/percentages`;
    let queryParams = new HttpParams()
      .set('flag_type', flagType)
      .set('start_month', this.filterStateService.filterData.start_month)
      .set('end_month', this.filterStateService.filterData.end_month);

    if (compareType === 'region') {
      queryParams = queryParams.set('regions', value);
    } else {
      queryParams = queryParams.set('markets', value);
    }

    const responseAsObs = this.http.get(url, { params: queryParams });
    return firstValueFrom(responseAsObs);
  }

  /**
   * Fetches compliance data for a specific region or market (for compare view)
   */
  async fetchComplianceDataForCompare(
    compareType: 'region' | 'market',
    value: string,
  ): Promise<RiskData> {
    const url = `${this.apiUrl}risk-analysis/regions`;
    let queryParams = new HttpParams();

    if (compareType === 'region') {
      queryParams = queryParams.set('regions', value);
    } else {
      queryParams = queryParams.set('markets', value);
    }

    queryParams = queryParams
      .set('start_month', this.filterStateService.filterData.start_month)
      .set('end_month', this.filterStateService.filterData.end_month);

    const responseAsObs = this.http.get<RiskData>(url, { params: queryParams });
    const data = await firstValueFrom(responseAsObs);
    return data;
  }

  async getAvailableMonths(): Promise<AvailableMonthsResponse> {
    const url = `${this.apiUrl}risk-analysis/months`;

    const cacheKey = 'available_months';
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached) as AvailableMonthsResponse;
    }

    const responseAsObs = this.http.get<AvailableMonthsResponse>(url);
    const data = await firstValueFrom(responseAsObs);
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    return data;
  }
}
